/**
 * more-in-color.js
 *
 * Powers sections/more-in-color.liquid: a single-row carousel of other styles
 * from the same brand available in the colour currently selected on the PDP.
 *
 * Data source: templates/collection.color-json.liquid, reached at
 *   /collections/<brand>?view=color-json&sort_by=best-selling
 *     &filter.v.option.color=<value>[&filter.v.option.color=<other casing>]
 *
 * Shopify's option-value facet is CASE SENSITIVE and this catalogue contains the
 * same colour under several casings ("Macadamia Latte-R" / "MACADAMIA LATTE-R"),
 * so every request sends up to four casings of the selected colour; Shopify ORs
 * repeated filter params, and casings that do not exist simply match nothing.
 */
(function () {
  'use strict';

  var COLOR_OPTION_NAMES = ['color', 'colour', 'farbe', 'couleur', 'kleur'];
  var DEBOUNCE_MS = 250;
  var IMAGE_WIDTHS = [240, 360, 540, 720];

  function colorCasings(value) {
    var out = [];
    var seen = Object.create(null);
    function add(v) {
      if (v && !seen[v]) {
        seen[v] = true;
        out.push(v);
      }
    }
    var lower = value.toLowerCase();
    add(value);
    add(value.toUpperCase());
    add(lower);
    add(
      lower.replace(/\b[a-z]/g, function (c) {
        return c.toUpperCase();
      })
    );
    return out;
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function escapeRegExp(s) {
    return String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  function isTrue(v) {
    return v === 'true' || v === '1';
  }

  /* ---------- reading the selected colour off the PDP ---------- */

  function optionSelectorScope(root) {
    var id = root.getAttribute('data-product-id');
    var scoped = id && document.querySelector('[data-product-id="' + id + '"] .option-selectors');
    return scoped || document.querySelector('.option-selectors');
  }

  function colorSelectorEl(root) {
    var scope = optionSelectorScope(root);
    if (!scope) return null;
    var wanted = (root.getAttribute('data-option-name') || 'Color').trim().toLowerCase();
    var selectors = scope.querySelectorAll('.option-selector');
    for (var i = 0; i < selectors.length; i++) {
      var label = selectors[i].querySelector('.label, legend');
      var text = '';
      if (label) {
        // Swatch-grid mode nests the selected value inside the legend
        // (<legend class="label">Color<span class="label__value">…</span></legend>),
        // so strip the value span before comparing option names.
        var clone = label.cloneNode(true);
        var valueSpan = clone.querySelector('.label__value');
        if (valueSpan) valueSpan.parentNode.removeChild(valueSpan);
        text = (clone.textContent || '').trim().toLowerCase();
      }
      if (text === wanted || COLOR_OPTION_NAMES.indexOf(text) !== -1) return selectors[i];
    }
    return null;
  }

  function readColor(root) {
    var sel = colorSelectorEl(root);
    if (!sel) return root.getAttribute('data-initial-color') || '';
    // Symmetry renders the colour option as a custom select (swatch dropdown)
    // when swatches are enabled, and as radios otherwise. Support both.
    var opt = sel.querySelector('.cc-select__option[aria-selected="true"]');
    if (opt && typeof opt.dataset.value === 'string' && opt.dataset.value !== '') {
      return opt.dataset.value;
    }
    var native = sel.querySelector('select');
    if (native && native.value) return native.value;
    var radio = sel.querySelector('input.js-option:checked, input[type="radio"]:checked');
    if (radio && radio.value) return radio.value;
    return root.getAttribute('data-initial-color') || '';
  }

  /* ---------- rendering ---------- */

  function srcsetFor(url) {
    if (!url || url.indexOf('width=') === -1) return '';
    return IMAGE_WIDTHS.map(function (w) {
      return url.replace(/width=\d+/, 'width=' + w) + ' ' + w + 'w';
    }).join(', ');
  }

  function cardHtml(item, opts) {
    var title = item.t || '';
    if (opts.stripVendor) {
      title = title.replace(/\s*\|\s*Buyers Club\s*$/i, '');
      if (opts.brand) {
        title = title.replace(new RegExp('\\s*\\bby\\s+' + escapeRegExp(opts.brand) + '\\s*$', 'i'), '');
      }
      if (!title) title = item.t;
    }
    var img = item.i || '';
    var media = img
      ? '<img class="mic__image" src="' +
        escapeHtml(img) +
        '" srcset="' +
        escapeHtml(srcsetFor(img)) +
        '" sizes="(min-width: 768px) 210px, 42vw" alt="' +
        escapeHtml(item.t || '') +
        '" loading="lazy" decoding="async"' +
        (item.iw ? ' width="' + item.iw + '" height="' + item.ih + '"' : '') +
        '>'
      : '';

    var onSale = !!item.c;
    var price =
      '<span class="mic__price' +
      (onSale ? ' mic__price--sale' : '') +
      '">' +
      (item.fr ? 'From ' : '') +
      escapeHtml(item.p || '') +
      (onSale ? '<s class="mic__was">' + escapeHtml(item.c) + '</s>' : '') +
      '</span>';

    var label =
      opts.showBrandLabel && item.b
        ? '<span class="mic__label">' + escapeHtml(item.b) + '</span>'
        : '';

    var oos = item.a === false ? '<span class="mic__oos">Backorder</span>' : '';

    return (
      '<li class="mic__item"><a class="mic__card" href="' +
      escapeHtml(item.u) +
      '"><span class="mic__media">' +
      media +
      '</span>' +
      label +
      '<span class="mic__title">' +
      escapeHtml(title) +
      '</span>' +
      price +
      oos +
      '</a></li>'
    );
  }

  /* ---------- the carousel ---------- */

  function MoreInColor(root) {
    this.root = root;
    this.row = root.querySelector('.mic__row');
    this.headingEl = root.querySelector('.mic__heading');
    this.eyebrowEl = root.querySelector('.mic__eyebrow');
    this.collection = root.getAttribute('data-collection');
    this.handle = root.getAttribute('data-product-handle');
    this.brand = root.getAttribute('data-brand') || '';
    this.max = parseInt(root.getAttribute('data-max'), 10) || 16;
    this.labelTpl = root.getAttribute('data-label') || '';
    this.headingTpl = root.getAttribute('data-heading') || '{color}';
    this.opts = {
      brand: this.brand,
      showBrandLabel: isTrue(root.getAttribute('data-show-brand-label')),
      stripVendor: isTrue(root.getAttribute('data-strip-vendor'))
    };
    this.hideBuyersClub = isTrue(root.getAttribute('data-hide-buyers-club'));
    this.cache = Object.create(null);
    this.pending = null;
    this.active = false;
    this.timer = null;
    this.token = 0;
  }

  MoreInColor.prototype.url = function (color) {
    var params = colorCasings(color)
      .map(function (v) {
        return 'filter.v.option.color=' + encodeURIComponent(v);
      })
      .join('&');
    return (
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root.replace(/\/$/, '')
        : '') +
      '/collections/' +
      encodeURIComponent(this.collection) +
      '?view=color-json&sort_by=best-selling&' +
      params
    );
  };

  MoreInColor.prototype.load = function (color) {
    var self = this;
    if (!color) return this.hide();
    if (this.cache[color]) return this.paint(color, this.cache[color]);

    var token = ++this.token;
    fetch(this.url(color), { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      })
      .then(function (text) {
        var start = text.indexOf('{');
        var end = text.lastIndexOf('}');
        if (start === -1 || end === -1) throw new Error('no json');
        var data = JSON.parse(text.slice(start, end + 1));
        self.cache[color] = data.items || [];
        if (token === self.token) self.paint(color, self.cache[color]);
      })
      .catch(function () {
        if (token === self.token) self.hide();
      });
  };

  MoreInColor.prototype.paint = function (color, items) {
    var self = this;
    var list = items.filter(function (it) {
      if (!it || !it.h || it.h === self.handle) return false;
      if (self.hideBuyersClub && it.h.indexOf('buyers-club') !== -1) return false;
      return true;
    });
    if (!list.length) return this.hide();

    list = list.slice(0, this.max);
    this.row.innerHTML = list
      .map(function (it) {
        return cardHtml(it, self.opts);
      })
      .join('');
    this.row.scrollLeft = 0;

    if (this.eyebrowEl) {
      this.eyebrowEl.textContent = this.labelTpl.replace('{brand}', this.brand).replace('{color}', color);
    }
    if (this.headingEl) {
      this.headingEl.textContent = this.headingTpl.replace('{brand}', this.brand).replace('{color}', color);
    }
    this.root.removeAttribute('hidden');
  };

  MoreInColor.prototype.hide = function () {
    this.root.setAttribute('hidden', '');
    this.row.innerHTML = '';
  };

  MoreInColor.prototype.sync = function () {
    var color = readColor(this.root);
    if (!this.active) {
      this.pending = color;
      return;
    }
    if (color === this.current) return;
    this.current = color;
    this.load(color);
  };

  MoreInColor.prototype.schedule = function () {
    var self = this;
    clearTimeout(this.timer);
    this.timer = setTimeout(function () {
      self.sync();
    }, DEBOUNCE_MS);
  };

  MoreInColor.prototype.activate = function () {
    if (this.active) return;
    this.active = true;
    this.pending = null;
    this.current = null;
    this.sync();
  };

  MoreInColor.prototype.init = function () {
    var self = this;

    // Any change inside the option selectors: radios fire a native change, and
    // Symmetry's custom select dispatches a bubbling CustomEvent('change').
    // Listen on document, not the .option-selectors node — the theme replaces
    // that node when it initialises the picker (swatch-grid mode), which left a
    // scoped listener attached to a detached element. sync() debounces and
    // early-returns when the colour is unchanged, so document-level is cheap.
    document.addEventListener(
      'change',
      function () {
        self.schedule();
      },
      true
    );

    // Belt and braces: the theme broadcasts every variant update on window via
    // jQuery, which native listeners do not see.
    if (window.jQuery) {
      window.jQuery(window).on('cc-variant-updated', function () {
        self.schedule();
      });
    }

    // Stay light on first paint: only fetch once the row is near the viewport.
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(
        function (entries) {
          for (var i = 0; i < entries.length; i++) {
            if (entries[i].isIntersecting) {
              io.disconnect();
              self.activate();
            }
          }
        },
        { rootMargin: '600px 0px' }
      );
      // The shell is [hidden], so observe a stable ancestor-anchored element.
      io.observe(this.root.parentNode || this.root);
    }

    // Fallback / safety net for browsers where the hidden shell never triggers
    // the observer. Deliberately waits for load + idle so the first paint and
    // the PDP's own assets are never competing with this request.
    var idle = window.requestIdleCallback || function (fn) { return setTimeout(fn, 1200); };
    var whenIdle = function () {
      idle(function () {
        self.activate();
      });
    };
    if (document.readyState === 'complete') {
      whenIdle();
    } else {
      window.addEventListener('load', whenIdle, { once: true });
    }
  };

  function boot() {
    var roots = document.querySelectorAll('[data-more-in-color]');
    for (var i = 0; i < roots.length; i++) {
      if (roots[i].dataset.micReady) continue;
      roots[i].dataset.micReady = '1';
      new MoreInColor(roots[i]).init();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
